import React, { useState, useEffect } from 'react';
import MobileLayout from '../../../components/layout/MobileLayout';
import { useI18n } from '../../../i18n/I18n';
import { getWoocommerceSiteStatus, deleteWoocommerceAuthorization, getMerchantPaymentSettings, getUserInfo } from '../../../services/useAPICaller';
import { PaymentSettings } from '../../../types/payment.types';

export default function WoocommerceView() {
    const { t } = useI18n();
    const [isConnecting, setIsConnecting] = useState(false);
    const [connectedSite, setConnectedSite] = useState<string | null>(null);
    const [user, setUser] = useState<any>(null);
    const [settings, setSettings] = useState<PaymentSettings | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const siteStatus = await getWoocommerceSiteStatus();
                if (siteStatus && siteStatus.siteUrl) {
                    setConnectedSite(siteStatus.siteUrl);
                }
            } catch (err) {
                console.error("Woocommerce not connected or error fetching status");
                setConnectedSite(null);
            }

            try {
                const userInfo = await getUserInfo();
                setUser(userInfo);
                await getMerchantPaymentSettings((data: PaymentSettings) => {
                    setSettings(data);
                });
            } catch (err) {
                console.error("Error loading user/settings");
            }
        };
        load();
    }, []);

    const handleConnect = () => {
        setIsConnecting(true);
        let baseUrl = settings?.businessUrl || "";
        if (baseUrl && !baseUrl.startsWith("http")) {
            baseUrl = `https://${baseUrl}`;
        }
        // Use user email for WC user_id mapping, fallback to settings mail or user id
        const userId = user?.email || settings?.mail || user?.id || "";

        let callbackUrl = `${process.env.REACT_APP_SPRINTCHECKOUT_BASE_URL}/payment_settings/woocommerce/authorization`;
        // Enforce HTTPS for the callback URL per requirements
        if (callbackUrl.startsWith("http://")) {
            callbackUrl = callbackUrl.replace("http://", "https://");
        }

        const returnUrl = `${window.location.origin}/#/admin/woocommerce`;

        const woocommerceUrl = `${baseUrl}/wc-auth/v1/authorize?app_name=Sprintcheckout&scope=read_write&user_id=${userId}&return_url=${encodeURIComponent(returnUrl)}&callback_url=${encodeURIComponent(callbackUrl)}`;

        window.location.href = woocommerceUrl;
    };

    const handleDisconnect = async () => {
        if (window.confirm(t('woocommerce.disconnectConfirm'))) {
            try {
                await deleteWoocommerceAuthorization();
                setConnectedSite(null);
            } catch (err) {
                console.error(t('woocommerce.errorDisconnecting'), err);
            }
        }
    };

    return (
        <MobileLayout>
            <div className="flex-1 flex flex-col h-full w-full animate-in fade-in slide-in-from-right duration-300 relative z-0">
                {/* Header */}
                <div className="px-6 lg:px-10 pt-14 lg:pt-10 pb-4 flex flex-col flex-shrink-0 relative z-10 w-full max-w-md lg:max-w-5xl mx-auto">
                    <button
                        onClick={() => window.location.hash = '#/admin/pos'}
                        className="w-10 h-10 rounded-full border-none flex items-center justify-center hover:bg-surface-hover transition-colors mb-6 cursor-pointer lg:hidden"
                        style={{ background: 'var(--color-surface)' }}
                    >
                        <span className="material-symbols-outlined text-txt-heading text-[20px]">arrow_back</span>
                    </button>
                    <h1 className="text-3xl font-bold text-txt-heading tracking-tight">{t('notifications.woocommerce.title')}</h1>
                </div>

                <div className="flex-1 flex flex-col overflow-y-auto no-scrollbar px-6 lg:px-10 pb-8 w-full max-w-md lg:max-w-5xl mx-auto">

                    {!connectedSite ? (
                        /* Empty State */
                        <div className="flex flex-col animate-in fade-in duration-500 flex-1">
                            <div className="rounded-3xl p-8 lg:p-10 flex flex-col flex-1 min-h-[500px]" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                                {/* Empty State Content (Two-Step Design) */}
                                <div className="flex flex-col flex-1 mt-6">

                                    {/* Step 1 */}
                                    <div className="flex gap-4 mb-10">
                                        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-surface-border)' }}>
                                            <span className="material-symbols-outlined text-[20px] text-electric-blue">help_outline</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <h3 className="text-xl font-bold text-txt-heading mb-2">{t('woocommerce.configurePlugin')}</h3>
                                            <p className="text-txt-secondary text-sm leading-relaxed mb-3">
                                                {t('woocommerce.copyApiKeyDesc')}
                                            </p>
                                            <button
                                                className="flex items-center gap-2 text-electric-blue font-bold hover:text-white transition-colors bg-transparent border-none p-0 cursor-pointer text-sm"
                                                onClick={() => setIsDrawerOpen(true)}
                                            >
                                                <span className="material-symbols-outlined text-[18px]">key</span>
                                                {t('woocommerce.apiKeysAndMore')}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Horizontal Divider */}
                                    <div className="h-[1px] w-full max-w-[80%] mx-auto my-12" style={{ background: 'var(--divider)' }}></div>

                                    {/* Step 2 */}
                                    <div className="flex gap-4 mb-auto">
                                        <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mt-1" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-surface-border)' }}>
                                            <span className="material-symbols-outlined text-[20px] text-electric-blue">link</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <h3 className="text-xl font-bold text-txt-heading mb-2">{t('woocommerce.linkStore')}</h3>
                                            <p className="text-txt-secondary text-sm leading-relaxed">
                                                {t('woocommerce.linkStoreDesc')}
                                            </p>
                                        </div>
                                    </div>

                                    {/* Bottom Connect Button */}
                                    <button
                                        onClick={handleConnect}
                                        disabled={isConnecting}
                                        className="w-full mt-10 bg-electric-blue hover:bg-electric-blue/90 text-white font-bold py-4 rounded-xl shadow-lg shadow-electric-blue/20 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 border-none cursor-pointer text-lg"
                                    >
                                        {isConnecting ? (
                                            <span className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin"></span>
                                        ) : (
                                            <>
                                                <span className="material-symbols-outlined text-[22px]">link</span>
                                                {t('woocommerce.connect')}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="mt-4 pb-4 flex justify-center">
                                <p className="text-xs text-txt-muted text-center max-w-[280px]"
                                   dangerouslySetInnerHTML={{ __html: t('woocommerce.moreInfoInDocs') }}
                                />
                            </div>
                        </div>
                    ) : (
                        /* Connected State */
                        <div className="flex flex-col animate-in fade-in duration-500 flex-1">
                            <div className="rounded-2xl p-6 flex flex-col flex-1" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                                {/* Header Row (Connected State) */}
                                <div className="flex justify-between items-center mb-8">
                                    <h2 className="text-xl font-bold text-txt-heading">{t('woocommerce.connectedSites')}</h2>
                                    <button
                                        className="bg-electric-blue hover:bg-electric-blue/90 text-white font-bold py-2 px-6 rounded-xl shadow-lg shadow-electric-blue/20 transition-all active:scale-95 text-sm border-none cursor-pointer"
                                    >
                                        {t('notifications.woocommerce.connect')}
                                    </button>
                                </div>

                                {/* Connected Site Card */}
                                <div className="glass-panel p-4 rounded-2xl flex items-center justify-between group" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-surface-border)' }}>
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-accent-purple flex items-center justify-center shadow-lg shadow-accent-purple/20">
                                            <span className="text-white font-bold text-xl">{connectedSite?.charAt(0).toUpperCase()}</span>
                                        </div>
                                        <div className="flex flex-col">
                                            <span className="text-txt-heading font-bold text-[16px] mb-1">{connectedSite}</span>
                                            <div className="flex items-center gap-1.5">
                                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]"></div>
                                                <span className="text-[10px] uppercase font-bold text-emerald-500 tracking-wider">
                                                    {t('notifications.woocommerce.connected')}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleDisconnect}
                                        className="w-10 h-10 rounded-xl flex items-center justify-center text-txt-muted hover:text-red-400 hover:bg-surface-hover transition-colors border-none cursor-pointer bg-transparent"
                                    >
                                        <span className="material-symbols-outlined text-[20px]">delete</span>
                                    </button>
                                </div>
                            </div>

                            <div className="mt-8 pt-8 pb-8 flex justify-center">
                                <p className="text-xs text-txt-muted text-center max-w-[280px]"
                                   dangerouslySetInnerHTML={{ __html: t('woocommerce.learnCreateKeys') }}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* API Keys Bottom Sheet Overlay */}
            <div className={`fixed inset-0 z-40 transition-opacity duration-300 ${isDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                {/* Backdrop */}
                <div
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                    onClick={() => setIsDrawerOpen(false)}
                />
            </div>

            {/* Drawer Content */}
            <div
                className={`fixed inset-x-0 bottom-0 z-50 bg-deep-navy rounded-t-3xl p-6 shadow-2xl transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] max-h-[90vh] overflow-y-auto w-full max-w-md lg:max-w-lg mx-auto lg:bottom-auto lg:top-1/2 lg:-translate-y-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:rounded-3xl ${isDrawerOpen ? 'translate-y-0 lg:translate-y-[-50%]' : 'translate-y-full lg:translate-y-full'
                    }`}
                style={{ borderTop: '1px solid var(--card-border)' }}
            >
                {/* Drag Handle */}
                <div className="w-12 h-1.5 rounded-full mx-auto mb-6" style={{ background: 'var(--divider)' }}></div>

                <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold text-txt-heading mb-2">{t('woocommerce.connectYourStore')}</h2>
                    <p className="text-txt-secondary text-sm">{t('woocommerce.quickSetup')}</p>
                </div>

                {/* Step 1: API Key */}
                <div className="flex gap-4 mb-8">
                    <div className="w-8 h-8 rounded-full bg-electric-blue/20 border border-electric-blue/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-electric-blue text-sm font-bold">1</span>
                    </div>
                    <div className="flex flex-col w-full">
                        <h3 className="text-lg font-bold text-txt-heading mb-1">{t('woocommerce.copyApiKey')}</h3>
                        <p className="text-txt-secondary text-sm leading-relaxed mb-4">
                            {t('woocommerce.copyApiKeyDesc2')}
                        </p>

                        <div className="rounded-xl p-4 flex items-center gap-4" style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
                            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'var(--color-surface)' }}>
                                <span className="material-symbols-outlined text-[20px] text-electric-blue">key</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs text-txt-muted font-medium mb-1 uppercase tracking-wider">{t('woocommerce.yourApiKey')}</span>
                                <span className="text-txt-secondary font-mono text-base font-medium">pk_live_...8f2a</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Step 2: WooCommerce Input */}
                <div className="flex gap-4 mb-8">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: 'var(--color-surface)', border: '1px solid var(--color-surface-border)' }}>
                        <span className="text-txt-muted text-sm font-bold">2</span>
                    </div>
                    <div className="flex flex-col w-full text-left">
                        <h3 className="text-lg font-bold text-txt-heading mb-1">{t('woocommerce.pasteInWooCommerce')}</h3>
                        <p className="text-txt-secondary text-sm leading-relaxed mb-4 text-left">
                            {t('woocommerce.pasteInWooCommerceDesc')}
                        </p>

                        <div className="bg-[#f8fafc] rounded-xl p-4 w-full text-left">
                            <div className="flex items-center gap-2 mb-4">
                                <div className="w-2.5 h-2.5 rounded-full bg-electric-blue"></div>
                                <span className="text-slate-800 font-bold text-sm">{t('woocommerce.woocommerceSettings')}</span>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">API KEY</span>
                                <div className="w-full h-10 border border-electric-blue/50 rounded-lg bg-white flex items-center px-3">
                                    <span className="text-slate-400 text-sm font-mono">{t('woocommerce.pasteHere')}</span>
                                </div>
                                <span className="text-[10px] text-slate-400 mt-2">{t('woocommerce.getApiKeysFromPanel')}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Divider & Action */}
                <div className="h-[1px] w-full mb-6" style={{ background: 'var(--divider)' }}></div>
                <button
                    onClick={() => {
                        setIsDrawerOpen(false);
                        window.location.hash = '#/admin/developers';
                    }}
                    className="w-full bg-electric-blue hover:bg-electric-blue/90 text-white font-bold py-4 rounded-xl shadow-lg shadow-electric-blue/20 transition-all active:scale-95 border-none cursor-pointer text-lg mb-2"
                >
                    {t('woocommerce.goToMyApiKey')}
                </button>
            </div>
        </MobileLayout>
    );
}
